import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'

import { DataPacket_Kind, RemoteParticipant, RoomEvent } from 'livekit-client'

import { useRoomContext } from '@/src/context'

import { IMessageSkill, TSkillsState } from '@/src/types/candidateSkills.type'
import { Skill } from './Skill/Skill'

import { useAppSelector } from '@/src/store/hooks'
import { selectIsAdmin } from '@/src/store/slices/meetData'
import { selectRoomType, selectV } from '@/src/store/slices/room'

import styles from './CandidateSkills.module.css'
import { CloseIcon } from '@/accets/icons'
import Image from 'next/image'

export const CandidateSkills = () => {
  const { t } = useTranslation()
  const room = useRoomContext()
  const isAdmin = useAppSelector(selectIsAdmin)
  const roomType = useAppSelector(selectRoomType)
  const v = useAppSelector(selectV)
  const shouldHideForTextAssistantHr = roomType === 'textAssistant' && v === 'hr'

  const [skills, setSkills] = useState<TSkillsState>({})
  const [isHidden, setIsHidden] = useState<boolean>(false)

  useEffect(() => {
    if (!room) {
      return
    }

    const handleDataReceived = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      kind?: DataPacket_Kind,
      topic?: string,
    ) => {
      if (topic?.includes('lk-chart-topic')) {
        try {
          const decoder = new TextDecoder('utf-8')
          const parsedSkills: IMessageSkill[] = JSON.parse(
            JSON.parse(JSON.parse(decoder.decode(payload))?.message)?.skills_json,
          )

          if (parsedSkills) {
            const newSkills = parsedSkills?.reduce(
              (acc, current) => ({ ...acc, [current?.skill_name]: current?.skill_value }),
              {},
            )

            setSkills(newSkills)
          }
        } catch (e) {
          console.error('Error with parsing skill: \n', e)
        }
      }
    }

    room?.on(RoomEvent.DataReceived, handleDataReceived)

    return () => {
      room?.off(RoomEvent.DataReceived, handleDataReceived)
    }
  }, [room])

  const isRecommendedCandidate = useMemo(
    () =>
      Object.values(skills)?.reduce((acc, current) => {
        acc = acc + current
        return acc
      }, 0) /
        Object.keys(skills)?.length >=
      70,
    [skills],
  )

  return (
    !shouldHideForTextAssistantHr &&
    Object.keys(skills)?.length > 0 &&
    isAdmin && (
      <AnimatePresence>
        <motion.div
          className={clsx(styles?.container, {
            [styles?.hiddenContainer]: isHidden,
          })}
          transition={{ duration: 0.25 }}
        >
          {isHidden ? (
            <motion.button
              onClick={() => setIsHidden(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              className={styles?.hiddenBtn}
            >
              <Image src="/icons/bar-chart.svg" alt="Show bars" width={16} height={16} />
            </motion.button>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.05 }}
              className={styles?.content}
            >
              <h4 className={styles?.header}>{t('candidateSkills.header')}</h4>
              <button className={styles?.closeIcon} onClick={() => setIsHidden(true)}>
                <CloseIcon />
              </button>

              <ul className={styles?.skillList}>
                {Object.keys(skills)?.map((skill) => (
                  <Skill key={skill} skill_title={skill} skill_value={skills[skill]} />
                ))}
              </ul>

              {isRecommendedCandidate ? (
                <p className={styles?.recommendedCandidate}>
                  ✅ {t('candidateSkills.recommendedCandidate')}
                </p>
              ) : (
                <p className={styles?.recommendedCandidate}>
                  ❌ {t('candidateSkills.notRecommendedCandidate')}
                </p>
              )}
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    )
  )
}
